import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Button, Modal, Input, Spin, Tag, message } from 'antd';
import { Plus, X, Image as ImageIcon } from 'react-feather';
import styles from './stylesTopLeagues.module.css';
import SortingTable from './SortingTable/SortingTable';
// Import AddNewIpPopup styles for consistent modal styling
import '../../../WhiteList/AddNewIpPopup/stylesAddNewIpPopup.css';
// Import API functions
import { API_ROUTES } from '../../../../api/ApiRoutes';
import createPrivateAdminPanelInstance from '../../../../api/PrivateAdminPanelInstance';
// Import icons for bet amount toggle


const TopLeagues = () => {
  const [isAddLeagueModalVisible, setIsAddLeagueModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedLeague, setSelectedLeague] = useState(null);
  
  // Get token and create API instance
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );
  
  // Clean up initial data to remove any undefined games
  const cleanLeaguesData = (data) => {
    return data.map(league => ({
      ...league,
      games: (league.games || []).filter(game => game && game.game_id)
    }));
  };

  const [leagues, setLeagues] = useState([]);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expandingLeagues, setExpandingLeagues] = useState(new Set()); // Track which leagues are being expanded
  const [betAmountType, setBetAmountType] = useState('cash'); // 'cash' or 'coin'

  // API Functions
  const fetchTopLeagues = async () => {
    try {
      setLoading(true);
      const response = await PrivateAdminPanelInstance.post(API_ROUTES.GET_TOP_LEAGUES_ORDERING, {
        include_deleted: false,
        include_inactive: true
      });
      
      if (response.data.success) {
        const leaguesData = response.data.data.top_leagues.map(league => ({
          id: league.entity_id,
          ename: league.league_name,
          sport: league.sport_name,
          order: league.display_order.toString(),
          is_active: league.is_active,
          icon: league.image_link,
          league_id: league.league_id,
          games: [] // Will be populated when expanded
        }));
        setLeagues(leaguesData);
      }
    } catch (error) {
      console.error('Error fetching top leagues:', error);
      message.error('Failed to fetch top leagues');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeagueEvents = async (leagueId) => {
    try {
      const response = await PrivateAdminPanelInstance.post(API_ROUTES.GET_EVENTS_BY_TOP_LEAGUE, {
        item_type: "FEATURED_EVENT",
        parent_type: "FEATURED_LEAGUE",
        parent_id: leagueId
      });
      
      if (response.data.success) {
        return response?.data?.data?.events_data?.map(event => ({
          game_id: event.event_id,
          name: event.event_name,
          eventDate: event.scheduled,
          status: event.status,
          country: event.country,
          location: event.country, // Using country as location for now
          provider: event.providers?.join(', ') || '',
          betsAmount: (event?.bet_volume?.rebet_cash || 0) + (event?.bet_volume?.rebet_free_cash || 0),
          numberOfBets: event.bet_count || 0,
          betType: "cash", // Default to cash, can be determined by bet_volume
          preMatchTier: 1,
          liveTier: 1,
          liveTimeDelay: 1,
          suggestedLive: "-",
          preMatchActivity: true,
          liveActivity: true,
          is_active: event.is_active,
          is_pinned: event.is_pinned,
          home_logo: event.home_logo && event.home_logo.trim() !== '' ? event.home_logo : null,
          away_logo: event.away_logo && event.away_logo.trim() !== '' ? event.away_logo : null,
          league_id: event.league_id, // Add league_id for pin functionality
        }));
      }
    } catch (error) {
      console.error('Error fetching league events:', error);
      message.error('Failed to fetch league events');
    }
    return [];
  };

  const handleAddLeague = () => {
    setIsAddLeagueModalVisible(true);
  };

  const togglePin = async (game) => {
    // Early return if game is invalid
    if (!game || !game.game_id) {
      console.warn('Invalid game passed to togglePin:', game);
      return;
    }

    try {
      const entityId = `FEATURED#EVENT#${game.league_id || 'unknown'}#${game.game_id}`;
      
      if (game.is_pinned) {
        // Unpin the event
        await PrivateAdminPanelInstance.post(API_ROUTES.UNPIN_FEATURED_EVENT, {
          entity_id: entityId
        });
      } else {
        // Pin the event
        await PrivateAdminPanelInstance.post(API_ROUTES.PIN_FEATURED_EVENT, {
          entity_id: entityId
        });
      }

      // Update local state
      setLeagues(prevLeagues =>
        prevLeagues.map(league => {
          const validGames = (league.games || []).filter(g => g && g.game_id);
          
          const updatedGames = validGames.map(g =>
            g.game_id === game.game_id
              ? { ...g, is_pinned: !g.is_pinned }
              : g
          );
          
          // If the game is being pinned, move it to the first position
          if (!game.is_pinned) {
            const pinnedGame = updatedGames.find(g => g && g.game_id === game.game_id);
            if (pinnedGame) {
              const otherGames = updatedGames.filter(g => g && g.game_id !== game.game_id);
              return {
                ...league,
                games: [pinnedGame, ...otherGames]
              };
            }
          }
          
          return {
            ...league,
            games: updatedGames
          };
        })
      );

      message.success(`Event ${game.is_pinned ? 'unpinned' : 'pinned'} successfully`);
    } catch (error) {
      console.error('Error toggling pin:', error);
      message.error('Failed to toggle pin status');
    }
  };

  const handleBetAmountTypeChange = (type) => {
    setBetAmountType(type);
  };

  // Don't filter leagues - keep all rows visible
  // The filtering will be handled in the display logic for individual cells
  const filteredLeaguesByBetType = leagues;

  const handleModalClose = () => {
    setIsAddLeagueModalVisible(false);
    setSearchValue('');
    setSelectedLeague(null);
    setSearchOptions([]);
  };

  // Real search function using API
  const fetchSearchOptions = useMemo(() => {
    let timer;
    return (query) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(async () => {
        if (!query || query.length < 2) {
          setSearchOptions([]);
          return;
        }
        setSearchLoading(true);
        try {
          const response = await PrivateAdminPanelInstance.post(API_ROUTES.GET_POTENTIAL_TOP_LEAGUES_AUTOCOMPLETE, {
            search_term: query,
            search_limit: 10
          });
          
          if (response.data.success) {
            const searchData = response.data.data.map(league => ({
              entity_id: league.entity_id,
              item_name: league.item_name,
              item_type: league.item_type,
              sport_id: league.sport_id,
              sport_name: league.sport_name,
              logo: league.logo_link
            }));
            setSearchOptions(searchData);
          }
        } catch (error) {
          console.error('Error searching leagues:', error);
          message.error('Failed to search leagues');
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    };
  }, []);

  useEffect(() => {
    fetchTopLeagues();
  }, []);

  useEffect(() => {
    fetchSearchOptions(searchValue);
  }, [searchValue, fetchSearchOptions]);

  // Function to handle league expansion and fetch events
  const handleLeagueExpand = async (league) => {
    if (league.games && league.games.length > 0) {
      return; // Already loaded
    }

    // Add league to expanding set
    setExpandingLeagues(prev => new Set([...prev, league.id]));

    try {
      const events = await fetchLeagueEvents(league.league_id);
      setLeagues(prevLeagues =>
        prevLeagues.map(l =>
          l.id === league.id
            ? { ...l, games: events }
            : l
        )
      );
    } catch (error) {
      console.error('Error fetching league events:', error);
      message.error('Failed to load league events');
    } finally {
      // Remove league from expanding set
      setExpandingLeagues(prev => {
        const newSet = new Set(prev);
        newSet.delete(league.id);
        return newSet;
      });
    }
  };

  const handleAddLeagueConfirm = async () => {
    if (!selectedLeague) return;
    setAddLoading(true);
    try {
      // Extract league_id from entity_id (format: LEAGUE#sr:tournament:xxx)
      const leagueId = selectedLeague.entity_id.replace('LEAGUE#', '');
      await PrivateAdminPanelInstance.post(API_ROUTES.CREATE_NEW_TOP_LEAGUE, {
        league_id: leagueId,
        league_name: selectedLeague.item_name,
        sport_id: selectedLeague.sport_id,
        sport_name: selectedLeague.sport_name
      });
      
      message.success('League added successfully');
      setIsAddLeagueModalVisible(false);
      setSearchValue('');
      setSelectedLeague(null);
      setSearchOptions([]);
      
      // Refresh the leagues list
      await fetchTopLeagues();
    } catch (error) {
      console.error('Error adding league:', error);
      message.error('Failed to add league');
    } finally {
      setAddLoading(false);
    }
  };

  const handleLeagueSelect = (league) => {
    setSelectedLeague(league);
  };

  const handleSearchClear = () => {
    setSearchValue('');
    setSearchOptions([]);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const toggleStatus = async (record) => {
    try {
      await PrivateAdminPanelInstance.post(API_ROUTES.SET_TOP_LEAGUE_ENTITY_ACTIVITY_STATUS, {
        entity_id: record.id,
        is_active: !record.is_active
      });
      
      message.success(`League ${!record.is_active ? 'activated' : 'deactivated'} successfully`);
      
      // Update local state
    setLeagues(prevData =>
      prevData.map(league =>
        league.id === record.id
          ? { ...league, is_active: !league.is_active }
          : league
      )
    );
    } catch (error) {
      console.error('Error toggling league status:', error);
      message.error('Failed to update league status');
    }
  };

  const removeLeague = async (record) => {
    try {
      await PrivateAdminPanelInstance.post(API_ROUTES.DELETE_TOP_LEAGUE, {
        entity_id: record.id
      });
      
      message.success('League deleted successfully');
      
      // Update local state
    setLeagues(prevData =>
      prevData.filter(league => league.id !== record.id)
    );
    } catch (error) {
      console.error('Error deleting league:', error);
      message.error('Failed to delete league');
    }
  };

  const changeOrder = async (recordOrLeagues, newOrder) => {
    let reorderedLeagues;

    // Check if first parameter is an array (from drag and drop) or object (from dropdown)
    if (Array.isArray(recordOrLeagues)) {
      // Called from drag and drop - leagues are already reordered
      reorderedLeagues = recordOrLeagues;
    } else {
      // Called from dropdown - need to reorder the leagues
      const record = recordOrLeagues;
      const newOrderNum = parseInt(newOrder);
      const currentOrderNum = parseInt(record.order);
      
      // If the order hasn't changed, return
      if (newOrderNum === currentOrderNum) {
        return;
      }

      // Create a copy of the leagues array
      const updatedLeagues = [...leagues];
      
      // Find the league being moved
      const leagueIndex = updatedLeagues.findIndex(league => league.id === record.id);
      if (leagueIndex === -1) return;
      
      // Remove the league from its current position
      const [movedLeague] = updatedLeagues.splice(leagueIndex, 1);
      
      // Insert the league at the new position (newOrder - 1 because arrays are 0-indexed)
      updatedLeagues.splice(newOrderNum - 1, 0, movedLeague);
      
      // Update all order numbers to reflect the new positions
      reorderedLeagues = updatedLeagues.map((league, index) => ({
        ...league,
        order: (index + 1).toString()
      }));
    }

    try {
      // Update local state first for immediate UI feedback
      setLeagues(reorderedLeagues);

      // Send API request to reorder
      const orderedIds = reorderedLeagues.map(league => league.league_id);
      await PrivateAdminPanelInstance.post(API_ROUTES.REORDER_TOP_LEAGUE_ENTITIES, {
        item_type: "FEATURED_LEAGUE",
        ordered_ids: orderedIds
      });

      message.success('League order updated successfully');
    } catch (error) {
      console.error('Error reordering leagues:', error);
      message.error('Failed to update league order');
      // Revert to original state on error
      fetchTopLeagues();
    }
  };

  // Use all leagues for table display (no filtering by search)
  const filteredLeagues = leagues;

  if (loading) {
    return (
      <div className={styles.container}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Top Leagues</h2>
        <Button
          type="primary"
          icon={<Plus size={16} />}
          onClick={handleAddLeague}
          className={styles.addButton}
        >
          Add League
        </Button>
      </div>

      <div className={styles.tableContainer}>
        <SortingTable
          columns={[
            '',
            '#Order',
            'Icon',
            'League',
            'ID',
            'Event name',
            '#Order',
            'Status',
            'Action',
            'Event date',
            'Live Status',
            'Country',
            'Location',
            'Provider',
            'Bets Amount',
            'Number of bets',
            'Pre-match Tier',
            'Live Tier',
            'Live Time Delay',
            'Suggested Live...',
            'Pre-match In...',
            'Live activity'
          ]}
          data={filteredLeaguesByBetType}
          setData={setLeagues}
          toggleStatus={toggleStatus}
          removeGame={removeLeague}
          setRemovingGameData={() => { }}
          etype="category"
          allGames={filteredLeaguesByBetType}
          addGameInTopLeagueEntity={() => { }}
          toggleTopLeagueEntityStatus={toggleStatus}
          changeTopLeagueEntityOrder={changeOrder}
          EditGameIcon={() => { }}
          onTogglePin={togglePin}
          betAmountType={betAmountType}
          onBetAmountTypeChange={handleBetAmountTypeChange}
          onLeagueExpand={handleLeagueExpand}
          expandingLeagues={expandingLeagues}
          rows={['order', 'icon', 'ename', 'blank', 'is_active']}
        />
      </div>

      <Modal
        closable={false}
        open={isAddLeagueModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={600}
        className="add-league-modal"
      >
        <div className="add-new-ip-panel">
          <div className="modal-header">
            <div className="modal-header-title">
              <h2 className="modal-title">Add League</h2>
            </div>
            <X onClick={handleModalClose} className="close-icon" />
          </div>
          <div className="divider" />
          <div className="msb-modal-body">
            <div className="autocomplete">
            <Input
                placeholder="Search leagues"
              value={searchValue}
              onChange={handleSearchChange}
              />
              {searchValue ? (
                <div>
                  {searchLoading ? (
                    <div className="center"><Spin size="small" /></div>
                  ) : (
                    searchOptions?.map((option) => (
                      <div
                        key={option.entity_id}
                        className={`${styles.leagueCard} ${selectedLeague?.entity_id === option.entity_id ? styles.selected : ''}`}
                        onClick={() => handleLeagueSelect(option)}
                      >
                        <div className={styles.leagueLogo}>
                          {/* <span className={styles.logoEmoji}>{option.logo_link}</span> */}
                          {option?.logo ? (
                            <img src={option?.logo} alt="League Logo" className={styles.logoEmoji}  />
                          ) : (
                            <ImageIcon size={18} />
                          )}
                        </div>
                        <div className={styles.leagueInfo}>
                          <span className={styles.leagueName}>{option.item_name}</span>
                        </div>
                        <div className={styles.sportTag}>
                          <span className={styles.sportText}>{option.sport_name}</span>
                        </div>
                      </div>
                    ))
                  )}
                  {!searchLoading && searchOptions.length === 0 && searchValue && (
                    <div className="empty-mini">No leagues found</div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
          <div className="modal-footer">
            <button className="cancel-button" onClick={handleModalClose} disabled={addLoading}>Close</button>
            <button 
              className={`submit-button ${!selectedLeague || addLoading ? 'disabled' : ''}`} 
              disabled={!selectedLeague || addLoading} 
              onClick={handleAddLeagueConfirm}
            >
              {addLoading ? 'Adding...' : 'Add League'}
            </button>
            </div>
        </div>
      </Modal>
    </div>
  );
};

export default TopLeagues;
